import {
  centerOrthoCamera,
  useCenterCameraOnPlaceholders,
} from "@/hooks/use-center-camera-on-placeholders";
import { useCached3DObjectIfExists } from "@/object-cache";
import { selectActiveElement } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { IElementOrthomosaic } from "@/types/ielement-extra-types";
import { isOrthographicCamera } from "@/types/threejs-type-guards";
import {
  Map2DControls,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import { FloorPlanBackgroundTransparency } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { JSX, useEffect } from "react";
import { SheetRenderer } from "./sheet-renderer";

type OrthoMosaicRendererProps = {
  /** The orthomosaic iElement */
  orthoMosaic: IElementOrthomosaic;
};

/**
 * @returns A 2D visualization of an orthomosaic image
 */
export function OrthoMosaicRenderer({
  orthoMosaic,
}: OrthoMosaicRendererProps): JSX.Element {
  const sheet = useCached3DObjectIfExists(orthoMosaic, "OrthoMosaicRenderer");

  assert(sheet, "OrthoMosaic to render must exist");

  const activeElement = useAppSelector(selectActiveElement);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!activeElement || activeElement.id !== orthoMosaic.id) {
      dispatch(setActiveElement(orthoMosaic.id));
    }
  }, [orthoMosaic, activeElement, dispatch]);

  const camera = useThree((s) => s.camera);
  const cameraData = useCenterCameraOnPlaceholders({
    // We only want to use the sheet for centering the camera.
    sheetElements: [orthoMosaic],
    placeholders: [],
  });

  // Re-center the camera, every time the sheet changes.
  useNonExhaustiveEffect(() => {
    if (isOrthographicCamera(camera)) {
      centerOrthoCamera(camera, cameraData);
    }
  }, [sheet, camera]);

  return (
    <>
      <SheetRenderer
        sheet={sheet}
        transparent={false}
        backgroundTransparent={FloorPlanBackgroundTransparency.No}
      />
      <Map2DControls camera={camera} />
    </>
  );
}
